import React from "react"
import { useTranslation } from "react-i18next"

import { CheckIcon, WarningIcon } from "components/Elements/Icons"

import { bikeAccountTypes, BIKE_STORE, BIKE_SOLD, bikeStatusList } from "utils/constants"

import dateService from "services/date"
import { useBikeStoreProcess } from "hooks/useBikeStoreProcess"
import { isNotEmpty } from "utils"

type BikeStatusInformationProps = {
	isAdmin: boolean
}

const BikeStatusInformation: React.FC<BikeStatusInformationProps> = ({ isAdmin }) => {
	const { bikeProcessState } = useBikeStoreProcess()
	const { currentBike: bike, bikeStatus, isSeller, isWalletSeller } = bikeProcessState
	const { t } = useTranslation()

	// Chaque vélo porte la notion d'"account_type", à savoir est-ce que le propriétaire du vélo à un compte ou non et si oui, sur quelle plateforme (Wallet, No wallet ou Garage à vélos)
	// Chaque compte est préfixé par le nom de l'opérateur (ex: BC_CONSO_ACCOUNT_VERIFIED)
	// Le tableau bikeAccountTypes liste tous les variantes de type de compte possibles
	// Pour savoir si le propriétaire a ajouté le vélo dans ce compte, on se base sur "mail_verified". Tant que ce n'est pas fait le boolean restera à false
	const isOwnerMailVerified = bike?.mail_verified
	const accountType = bikeAccountTypes.find(({ suffix }) => {
		// On laisse le type de vélo (provenant de account_type) si le mail est vérifié ou si le type de vélo est "_WALLET_NOT_MIGRATED" ou "_NOWALLET_NOT_MIGRATED" (car ce sont d'anciens comptes qui n'ont pas été migrés dans le nouveau système de compte)
		return isOwnerMailVerified ||
			["_WALLET_NOT_MIGRATED", "_NOWALLET_NOT_MIGRATED"].includes(suffix)
			? bike?.account_type === `${bike?.operator}${suffix}`
			: `${bike?.operator}_CONSO_NOT_VERIFIED` === `${bike?.operator}${suffix}`
	})

	// Si le propriétaire du vélo à un compte (account_id),
	// on souhaite afficher le numéro de ce compte à la place du message : "Le vélo est géré dans le compte du propriétaire"
	const isBikeOwnerHasAnAccount = bike?.account_id && isNotEmpty(bike.account_id)

	const isWalletNotMigratedAccountType =
		accountType?.id === "wallet_not_migrated" || accountType?.id === "nowallet_not_migrated"

	return (
		<>
			{/* Specific message for BIKE_STORE */}
			{!isAdmin && bikeStatus === BIKE_STORE && (
				<>
					{isSeller && <p className={"mb-2"}>{t("status.is_seller", { ns: "bike" })}</p>}

					{!isSeller && (
						<p className={"text-danger mb-2"}>
							{t("status.is_not_seller", { ns: "bike" })} <br />
							{t("search_global_error", { ns: "bike" })}
						</p>
					)}
				</>
			)}
			{/* Specific info for isSeller || isAdmin */}
			{(isSeller || isAdmin) && (
				<>
					<div className="mb-2 flex items-center">
						{accountType?.label && (
							<p className="u-mg-right-s">
								{isBikeOwnerHasAnAccount
									? t("account_type.has_account_id", {
											ns: "bike",
											account_id: bike?.account_id,
										})
									: t(`${accountType?.label}`)}
							</p>
						)}
						{accountType?.iconLabel === "check" && (
							<CheckIcon size={20} color="#4caf50" />
						)}
						{accountType?.iconLabel === "warning" && (
							<WarningIcon size={20} color="var(--warningColor)" />
						)}
					</div>
					{bike?.bike_last_registered_by?.create_date &&
						!isWalletNotMigratedAccountType && (
							<p className="text-xs">
								{t("account_type.date", { ns: "bike" })} :{" "}
								{dateService.formatDate(
									bike.bike_last_registered_by.create_date,
									"dd/MM/y"
								)}
							</p>
						)}
				</>
			)}

			{!(isSeller || isAdmin) &&
				bikeStatusList.find((s) => s.value === bikeStatus)?.description && (
					<p className="mb-2">
						{bikeStatusList.find((s) => s.value === bikeStatus)?.description}
					</p>
				)}

			{bikeStatus === BIKE_SOLD && !(isSeller || isAdmin || isWalletSeller) && (
				<p className="">{t("edit_bike_account_not_seller", { ns: "bike" })}</p>
			)}

			{bikeStatus === BIKE_SOLD && isWalletSeller && (
				<p className={"mb-2"}>{t("status.sold", { ns: "bike" })}</p>
			)}

			{/* Bike is null and manufacturer describe bike => example with MFC */}
			{bikeStatus === BIKE_STORE && bike?.description_made_by === "MANUFACTURER" && (
				<p className={"mb-2"}>
					{t("pre_registration.manufaturer_made_description", { ns: "bike" })}
				</p>
			)}

			{/* Pre-registration by store */}
			{bikeStatus === BIKE_STORE && bike?.description_made_by === "STORE" && (
				<p className={"mb-2"}>
					{t("pre_registration.store_description_modification", { ns: "bike" })}
				</p>
			)}
		</>
	)
}

export default BikeStatusInformation
